<template>
  <div class="merchant-container">
    <div class="side-nav">
      <MerchantSideNav />
      <MenuToggle />
    </div>
    <div class="merchant-body">
      <header>
        <MerchantHeader />
      </header>
      <div class="dashboard-content">
        <div class="row align-items-center justify-content-between m-0">
          <h3>Dashboard</h3>
          <span class="env-badge" :class="isSandbox ? 'is-sandbox' : 'is-live'">
            {{ dashboardEnvironment }}
          </span>
        </div>

        <!-- An empty Sandbox looks identical to a broken dashboard: every payment figure
             reads 0 while the account-level ones (balance, API keys, KYC) still show.
             Say so explicitly rather than letting the zeros speak for themselves. -->
        <div class="row" v-if="showSandboxEmptyNotice">
          <div class="col-12 pl-0">
            <div class="sandbox-empty">
              <span class="material-icons">science</span>
              <div>
                <strong>You are viewing Sandbox.</strong>
                There are no test payments on this account yet, so every payment figure below
                is zero. Your balance, API keys and KYC status are account-level and show either way.
                <button type="button" class="link-btn" @click="switchToLive">Switch to Live</button>
              </div>
            </div>
          </div>
        </div>

        <!-- KPI CARDS -->
        <div class="row mb-4">
          <div class="col-12 col-lg-6 col-xl-3 col-md-12 mb-3">
            <div class="card-style">
              <div class="d-flex align-items-center justify-content-between">
                <span>Total Balance</span>
                <CurrencySelector
                  v-model="displayCurrency"
                  compact
                  @change="onCurrencyChange"
                />
              </div>
              <h2>{{ currencySymbol }} {{ money(balance.amount) }}</h2>
              <!-- Display conversion at the mid rate. Settlement is priced separately. -->
              <small class="muted" v-if="showsEquivalent">
                {{ equivalentBalance }}
                <span v-if="merchantCurrencyNote"> &middot; {{ merchantCurrencyNote }}</span>
              </small>
            </div>
          </div>
          <div class="col-12 col-lg-6 col-xl-3 col-md-12 mb-3">
            <div class="card-style">
              <span>Today's Successful Transaction</span>
              <h2 class="success">{{ num(payments.today && payments.today.paid) }}</h2>
              <small class="muted">{{ currencySymbol }} {{ money(payments.today && payments.today.revenue) }}</small>
            </div>
          </div>
          <div class="col-12 col-lg-6 col-xl-3 col-md-12 mb-3">
            <div class="card-style">
              <router-link :to="{ name: 'merchant-success-transaction' }" style="text-decoration: none;">
                <span>Total Succesful Transaction</span>
                <h2 class="success">{{ num(payments.paid) }}</h2>
                <small class="muted">of {{ num(payments.total) }} total</small>
              </router-link>
            </div>
          </div>
          <div class="col-12 col-lg-6 col-xl-3 col-md-12 mb-3">
            <div class="card-style">
              <router-link :to="{ name: 'merchant-failed-transaction' }" style="text-decoration: none;">
                <span>Pending / Failed</span>
                <h2 class="failed">{{ num(payments.pending) + num(payments.failed) }}</h2>
                <small class="muted">
                  {{ num(payments.pending) }} pending &middot; {{ num(payments.failed) }} failed
                </small>
              </router-link>
            </div>
          </div>
        </div>

        <!-- REVENUE + SUCCESS RATE -->
        <div class="row mb-4">
          <div class="col-12 col-lg-6 col-xl-3 col-md-12 mb-3">
            <div class="card-style">
              <span>Gross Revenue</span>
              <h2>{{ currencySymbol }} {{ money(payments.gross_revenue) }}</h2>
              <!-- Shown only when the API supplies them. Deriving a net total from
                   recent_payments would be wrong - that is only the latest few rows. -->
              <small class="muted" v-if="payments.net_revenue !== undefined">
                Net {{ currencySymbol }} {{ money(payments.net_revenue) }}
                <span v-if="payments.total_fees !== undefined">
                  &middot; fees {{ currencySymbol }} {{ money(payments.total_fees) }}
                </span>
              </small>
              <small class="muted" v-else>before processing fees</small>
            </div>
          </div>
          <div class="col-12 col-lg-6 col-xl-3 col-md-12 mb-3">
            <div class="card-style">
              <span>Success Rate</span>
              <h2 class="success">{{ num(payments.success_rate) }}%</h2>
              <div class="rate-bar"><div class="rate-fill" :style="{ width: successRateWidth }"></div></div>
            </div>
          </div>
          <div class="col-12 col-lg-6 col-xl-3 col-md-12 mb-3">
            <div class="card-style">
              <span>Active API Keys</span>
              <h2>{{ num(account.active_api_keys) }}</h2>
              <small class="muted">{{ num(account.active_webhooks) }} active webhooks</small>
            </div>
          </div>
          <div class="col-12 col-lg-6 col-xl-3 col-md-12 mb-3">
            <div class="card-style">
              <span>KYC Status</span>
              <h2 :class="account.kyc_status === 'verified' ? 'success' : 'pending-text'">
                {{ account.kyc_status || '-' }}
              </h2>
              <small class="muted">{{ account.merchant_code || '' }}</small>
            </div>
          </div>
        </div>

        <!-- CHART -->
        <div class="row mb-4">
          <div class="col-12 pl-0 pr-0">
            <div class="card-style">
              <div class="row align-items-center justify-content-between m-0 mb-2">
                <div>
                  <h3 class="mb-0">Revenue</h3>
                  <small class="muted" v-if="analyticsRange.start">
                    {{ analyticsRange.start }} to {{ analyticsRange.end }}
                  </small>
                </div>
                <div class="range-tabs">
                  <button
                    type="button"
                    v-for="opt in rangeOptions"
                    :key="opt.value"
                    class="range-btn"
                    :class="days === opt.value ? 'is-active' : ''"
                    @click="setRange(opt.value)"
                  >{{ opt.text }}</button>
                </div>
              </div>

              <div v-if="!chart.points.length" class="chart-empty">
                No analytics data for this period.
              </div>
              <div v-else class="chart-wrap">
                <svg :viewBox="'0 0 ' + chart.w + ' ' + chart.h" class="chart-svg" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#0066b3" stop-opacity="0.28" />
                      <stop offset="100%" stop-color="#0066b3" stop-opacity="0.02" />
                    </linearGradient>
                  </defs>

                  <!-- grid + y labels -->
                  <g>
                    <line
                      v-for="(g, i) in chart.grid"
                      :key="'g' + i"
                      :x1="chart.padL" :x2="chart.w - chart.padR"
                      :y1="g.y" :y2="g.y"
                      stroke="#eceff3" stroke-width="1"
                    />
                    <text
                      v-for="(g, i) in chart.grid"
                      :key="'t' + i"
                      :x="chart.padL - 8" :y="g.y + 4"
                      text-anchor="end" class="axis-text"
                    >{{ g.label }}</text>
                  </g>

                  <!-- area + line -->
                  <polygon :points="chart.area" fill="url(#revFill)" />
                  <polyline :points="chart.line" fill="none" stroke="#0066b3" stroke-width="2"
                    stroke-linejoin="round" stroke-linecap="round" />

                  <!-- points -->
                  <g>
                    <circle
                      v-for="(p, i) in chart.points"
                      :key="'p' + i"
                      :cx="p.x" :cy="p.y" :r="p.value > 0 ? 3.5 : 2"
                      :fill="p.value > 0 ? '#0066b3' : '#c9d3dd'"
                    >
                      <title>{{ p.date }} — {{ currencySymbol }} {{ money(p.value) }} ({{ p.count }} paid)</title>
                    </circle>
                  </g>

                  <!-- x labels -->
                  <text
                    v-for="(p, i) in chart.xLabels"
                    :key="'x' + i"
                    :x="p.x" :y="chart.h - 8"
                    text-anchor="middle" class="axis-text"
                  >{{ p.label }}</text>
                </svg>
              </div>

              <div class="row m-0 mt-2" v-if="analyticsTotals.gross_revenue !== undefined">
                <small class="muted">
                  Period total: <strong>{{ currencySymbol }} {{ money(analyticsTotals.gross_revenue) }}</strong>
                  &middot; {{ num(analyticsTotals.paid_count) }} paid of {{ num(analyticsTotals.total_count) }}
                  &middot; {{ num(analyticsTotals.success_rate) }}% success
                </small>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-4">
          <!-- CASH FLOW (derived from the daily series) -->
          <div class="col-12 col-xl-7 col-lg-12 col-md-12 mb-3 pl-0">
            <div class="card-style">
              <h3>Cash Flow</h3>
              <div class="row">
                <div class="col-12 col-lg-4">
                  <span>Today's</span>
                  <h4>{{ currencySymbol }} {{ money(cashFlow.today) }}</h4>
                </div>
                <div class="col-12 col-lg-4">
                  <span>Last 7 Days</span>
                  <h4>{{ currencySymbol }} {{ money(cashFlow.week) }}</h4>
                </div>
                <div class="col-12 col-lg-4">
                  <span>Last 30 Days</span>
                  <h4>{{ currencySymbol }} {{ money(cashFlow.month) }}</h4>
                </div>
              </div>
            </div>
          </div>

          <!-- STATUS BREAKDOWN -->
          <div class="col-12 col-xl-5 col-lg-12 col-md-12 mb-3 pl-0">
            <div class="card-style">
              <h3>Status Breakdown</h3>
              <div class="status-row" v-for="(s, i) in statusBreakdown" :key="'s' + i">
                <span class="status-name">{{ s.label }}</span>
                <div class="status-track">
                  <div class="status-fill" :class="s.cls" :style="{ width: s.width }"></div>
                </div>
                <span class="status-count">{{ s.count }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-5">
          <!-- RECENT PAYMENTS -->
          <div class="col-12 col-xl-7 col-lg-12 mb-3 pl-0">
            <div class="card-style">
              <div class="row align-items-center justify-content-between m-0 mb-2">
                <h3 class="mb-0">Recent Payments</h3>
                <router-link :to="{ name: 'merchant-success-transaction' }" class="view-all">View all</router-link>
              </div>
              <div v-if="!recentPayments.length" class="muted">No payments yet.</div>
              <table class="mini-table" v-else>
                <thead>
                  <tr>
                    <th>Order</th><th>Payer</th><th>Date</th><th>Status</th>
                    <th class="text-right">Amount</th><th class="text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in recentPayments" :key="'rp' + i">
                    <td>{{ p.purchase_order_id || '-' }}</td>
                    <td class="ellipsis">{{ p.payer_email || '-' }}</td>
                    <td>{{ shortDate(p.created_at) }}</td>
                    <td>
                      <span class="status-pill" :class="statusClass(p.status)">
                        {{ p.status_label || p.status }}
                      </span>
                    </td>
                    <td class="text-right">{{ p.currency }} {{ money(grossOf(p)) }}</td>
                    <td class="text-right">
                      <strong>{{ p.currency }} {{ money(netOf(p)) }}</strong>
                      <div class="fee-note" v-if="feeOf(p) > 0">
                        &minus;{{ money(feeOf(p)) }} fee
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- TOP CUSTOMERS -->
          <div class="col-12 col-xl-5 col-lg-12 mb-3 pl-0">
            <div class="card-style">
              <h3>Top Customers</h3>
              <div class="row m-0 mb-2">
                <small class="muted">
                  {{ num(customerTotals.unique_customers) }} unique &middot;
                  {{ num(customerTotals.repeat_customers) }} repeat
                </small>
              </div>
              <div v-if="!topCustomers.length" class="muted">No customers yet.</div>
              <table class="mini-table" v-else>
                <thead>
                  <tr><th>Customer</th><th class="text-right">Payments</th><th class="text-right">Revenue</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(c, i) in topCustomers" :key="'tc' + i">
                    <td class="ellipsis">{{ c.payer_email || '-' }}</td>
                    <td class="text-right">{{ num(c.payments) }}</td>
                    <td class="text-right">{{ currencySymbol }} {{ money(c.revenue) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footer>
      <MerchantFooter />
    </footer>
  </div>
</template>
<script>
import MerchantHeader from "./MerchantHeader.vue";
import MerchantFooter from "./MerchantFooter.vue";
import MerchantSideNav from "./MerchantSideNav.vue";
import MenuToggle from "./MenuToggle.vue";
import { mapActions, mapState } from "vuex";
import CurrencySelector from "../CurrencySelector.vue";
import {
  convert, formatCurrency, findRate, currencyForCountry,
  getDisplayCurrency, setDisplayCurrency, hasDisplayCurrency, BASE_CURRENCY,
} from "@/utils/currency";

export default {
  components: { MerchantHeader, MerchantFooter, MerchantSideNav, MenuToggle },
  data() {
    return {
      days: 30,
      // A merchant views in the currency of their country of business by default.
      displayCurrency: getDisplayCurrency(BASE_CURRENCY),
      rangeOptions: [
        { value: 7, text: "7D" },
        { value: 30, text: "30D" },
        { value: 90, text: "90D" },
      ],
    };
  },
  computed: {
    ...mapState([
      "merchantDashboard",
      "merchantAnalytics",
      "merchantCustomers",
      "merchantEnvironment",
      "currencies",
      "currencyCountries",
      "kycdata",
      "profile",
    ]),
    isSandbox: function () { return this.merchantEnvironment === "sandbox"; },
    dashboardEnvironment: function () {
      return this.merchantDashboard.environment || this.merchantEnvironment || "live";
    },
    account: function () { return this.merchantDashboard.account || {}; },
    balance: function () { return this.merchantDashboard.balance || {}; },
    payments: function () { return this.merchantDashboard.payments || {}; },
    recentPayments: function () {
      const list = this.merchantDashboard.recent_payments;
      return Array.isArray(list) ? list.slice(0, 6) : [];
    },
    currencySymbol: function () {
      const c = this.balance.currency || "USD";
      return c === "USD" ? "$" : c;
    },
    successRateWidth: function () {
      const r = Number(this.payments.success_rate) || 0;
      return Math.max(0, Math.min(100, r)) + "%";
    },
    analyticsRange: function () { return this.merchantAnalytics.range || {}; },
    analyticsTotals: function () { return this.merchantAnalytics.totals || {}; },
    dailySeries: function () {
      const d = this.merchantAnalytics.daily;
      return Array.isArray(d) ? d : [];
    },
    customerTotals: function () { return this.merchantCustomers.totals || {}; },
    topCustomers: function () {
      const list = this.merchantCustomers.top_customers;
      return Array.isArray(list) ? list.slice(0, 6) : [];
    },
    /**
     * Status counts come from analytics when it supplies them, and otherwise from the
     * DASHBOARD's own payments block - which carries paid / pending / failed /
     * cancelled / expired directly.
     *
     * Previously this read analytics only, so whenever merchant/analytics returned no
     * status_breakdown the whole panel sat at zero even though the dashboard payload
     * had the numbers right there. Cancelled is styled neutrally: a payment the buyer
     * abandoned is not a failure.
     */
    /** Everything the merchant's own currency depends on. */
    showsEquivalent: function () {
      return this.displayCurrency &&
             this.displayCurrency !== BASE_CURRENCY &&
             Boolean(findRate(this.currencies, this.displayCurrency));
    },
    equivalentBalance: function () {
      const value = convert(this.currencies, this.balance.amount,
                            this.balance.currency || BASE_CURRENCY, this.displayCurrency);
      if (value === null) { return ""; }
      return formatCurrency(this.currencies, value, this.displayCurrency);
    },
    /**
     * A merchant's currency follows the country of business rather than a personal
     * preference, so say where the figure came from when it matches.
     */
    merchantCurrencyNote: function () {
      const country = this.businessCountry;
      if (!country) { return ""; }
      const derived = currencyForCountry(this.currencyCountries, country);
      return (derived === this.displayCurrency) ? (country + " business currency") : "";
    },
    businessCountry: function () {
      const kyc = (this.kycdata && (this.kycdata.kyc || this.kycdata)) || {};
      const author = (this.profile && this.profile.author) || {};
      return kyc.country || kyc.business_country || author.country || "";
    },

    /** Sandbox, loaded, and genuinely nothing in it. */
    showSandboxEmptyNotice: function () {
      if (this.dashboardEnvironment !== "sandbox") { return false; }
      const p = this.merchantDashboard.payments;
      if (!p) { return false; }
      return Number(p.total || 0) === 0;
    },
    statusBreakdown: function () {
      const fromAnalytics = this.merchantAnalytics.status_breakdown || {};
      const fromPayments = this.merchantDashboard.payments || {};
      const has = function (o) {
        return o && ["paid", "pending", "failed", "cancelled", "expired"].some(function (k) {
          return o[k] !== undefined && o[k] !== null;
        });
      };
      const b = has(fromAnalytics) ? fromAnalytics : fromPayments;
      const map = [
        { key: "paid", label: "Paid", cls: "is-paid" },
        { key: "pending", label: "Pending", cls: "is-pending" },
        { key: "failed", label: "Failed", cls: "is-failed" },
        { key: "cancelled", label: "Cancelled", cls: "is-cancelled" },
        { key: "expired", label: "Expired", cls: "is-cancelled" },
      ];
      let max = 0;
      map.forEach(function (m) { max = Math.max(max, Number(b[m.key]) || 0); });
      return map.map(function (m) {
        const count = Number(b[m.key]) || 0;
        return {
          label: m.label,
          cls: m.cls,
          count: count,
          width: (max ? (count / max) * 100 : 0) + "%",
        };
      });
    },
    /** Today / last 7 / last 30 derived from the daily revenue series. */
    cashFlow: function () {
      const d = this.dailySeries;
      const sum = function (arr) {
        return arr.reduce(function (a, x) { return a + (Number(x.revenue) || 0); }, 0);
      };
      return {
        today: d.length ? (Number(d[d.length - 1].revenue) || 0) : 0,
        week: sum(d.slice(-7)),
        month: sum(d.slice(-30)),
      };
    },
    /** Dependency-free SVG line/area chart of daily revenue. */
    chart: function () {
      const data = this.dailySeries;
      const w = 720, h = 260, padL = 52, padR = 16, padT = 16, padB = 30;
      const empty = { w: w, h: h, padL: padL, padR: padR, points: [], line: "", area: "", grid: [], xLabels: [] };
      if (!data.length) { return empty; }

      const innerW = w - padL - padR;
      const innerH = h - padT - padB;
      const n = data.length;
      const step = n > 1 ? innerW / (n - 1) : 0;

      let maxV = 0;
      data.forEach(function (d) { maxV = Math.max(maxV, Number(d.revenue) || 0); });
      // round the axis up to something readable; never divide by zero
      const niceMax = maxV <= 0 ? 1 : Math.ceil(maxV * 1.15);

      const points = data.map(function (d, i) {
        const v = Number(d.revenue) || 0;
        return {
          x: padL + (n > 1 ? i * step : innerW / 2),
          y: padT + innerH - (v / niceMax) * innerH,
          value: v,
          count: Number(d.paid_count) || 0,
          date: d.date,
        };
      });

      const line = points.map(function (p) { return p.x.toFixed(1) + "," + p.y.toFixed(1); }).join(" ");
      const first = points[0], last = points[points.length - 1];
      const area = padL + "," + (padT + innerH) + " " + line + " " + last.x.toFixed(1) + "," + (padT + innerH);

      const gridCount = 4;
      const grid = [];
      for (let g = 0; g <= gridCount; g++) {
        const value = (niceMax / gridCount) * (gridCount - g);
        grid.push({
          y: padT + (innerH / gridCount) * g,
          label: value >= 1000 ? (value / 1000).toFixed(1) + "k" : value.toFixed(0),
        });
      }

      // at most 6 x labels so they never overlap
      const every = Math.max(1, Math.ceil(n / 6));
      const xLabels = [];
      points.forEach(function (p, i) {
        if (i % every === 0 || i === n - 1) {
          const parts = String(p.date).split("-");
          xLabels.push({ x: p.x, label: parts.length === 3 ? parts[2] + "/" + parts[1] : p.date });
        }
      });

      return { w: w, h: h, padL: padL, padR: padR, points: points, line: line, area: area, grid: grid, xLabels: xLabels };
    },
  },
  created() {
    this.loadAll();
    // Both cached for 24h, so this is normally free.
    this.listCurrencies();
    const vm = this;
    this.listCurrencyCountries().then(function () { vm.applyBusinessCurrency(); });
  },
  watch: {
    merchantEnvironment: function () { this.loadAll(); },
  },
  methods: {
    ...mapActions([
      "getMerchantDashboard", "getMerchantAnalytics", "getMerchantCustomers",
      "listCurrencies", "listCurrencyCountries",
    ]),

    /**
     * Merchant default currency comes from the country of business, not a personal
     * setting. Only applied when the merchant has not already picked a view currency -
     * overriding an explicit choice on every page load would be maddening.
     */
    applyBusinessCurrency() {
      if (hasDisplayCurrency()) { return; }
      const country = this.businessCountry;
      if (!country) { return; }
      const code = currencyForCountry(this.currencyCountries, country);
      if (code) { this.displayCurrency = code; }
    },

    onCurrencyChange(code) {
      this.displayCurrency = code;
      setDisplayCurrency(code);
    },

    loadAll() {
      const env = this.merchantEnvironment;
      this.getMerchantDashboard({ environment: env });
      this.getMerchantAnalytics({ environment: env, days: this.days });
      this.getMerchantCustomers({ environment: env });
    },
    setRange(days) {
      if (this.days === days) { return; }
      this.days = days;
      this.getMerchantAnalytics({ environment: this.merchantEnvironment, days: days });
    },
    num(v) {
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    },
    money(v) {
      const n = Number(v);
      return (isNaN(n) ? 0 : n).toFixed(2);
    },
    shortDate(value) {
      if (!value) { return "-"; }
      const d = new Date(value);
      if (isNaN(d.getTime())) { return String(value); }
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    },
    /**
     * Money off a payment row, preferring the *_cents integer.
     * The API sends both `amount` and `amount_cents`; the integer avoids the float
     * drift that makes gross - fee != net by a cent once a percentage fee applies.
     */
    centsOf(row, key) {
      if (!row) { return 0; }
      const cents = row[key + "_cents"];
      if (cents !== undefined && cents !== null && cents !== "") {
        const c = Number(cents);
        if (!isNaN(c)) { return c / 100; }
      }
      const n = Number(row[key]);
      return isNaN(n) ? 0 : n;
    },
    grossOf(row) { return this.centsOf(row, "amount"); },
    feeOf(row) { return this.centsOf(row, "fee"); },
    /** Falls back to gross - fee if an older payload omits net_amount. */
    netOf(row) {
      if (!row) { return 0; }
      const hasNet = (row.net_amount_cents !== undefined && row.net_amount_cents !== null) ||
                     (row.net_amount !== undefined && row.net_amount !== null);
      return hasNet ? this.centsOf(row, "net_amount") : (this.grossOf(row) - this.feeOf(row));
    },

    switchToLive() {
      this.$store.commit("setMerchantEnvironment", "live");
    },
    statusClass(status) {
      const c = String(status || "").toLowerCase();
      if (["cancelled", "canceled", "expired"].indexOf(c) !== -1) { return "is-cancelled"; }
      const s = String(status || "").toLowerCase();
      if (["paid", "completed", "success"].indexOf(s) !== -1) { return "is-success"; }
      if (["pending", "initiated", "processing"].indexOf(s) !== -1) { return "is-pending"; }
      return "is-failed";
    },
  },
};
</script>
<style lang="scss" scoped>
.muted { color: #6c757d; font-size: 12px; display: block; }
.pending-text { color: #e08b00; text-transform: capitalize; }
.env-badge {
  font-size: 12px; font-weight: 700; padding: 3px 12px; border-radius: 12px;
  text-transform: uppercase; letter-spacing: 0.4px;
}
.env-badge.is-live { background: #e6f0f8; color: #0066b3; }
.env-badge.is-sandbox { background: #fdf3e0; color: #e08b00; }

.rate-bar { height: 6px; background: #eef1f4; border-radius: 3px; margin-top: 8px; overflow: hidden; }
.rate-fill { height: 100%; background: #00a800; border-radius: 3px; }

.range-tabs { display: inline-flex; border: 1px solid #e3e7eb; border-radius: 16px; overflow: hidden; }
.range-btn {
  border: none; background: #fff; color: #6c757d; font-size: 12px; font-weight: 600;
  padding: 4px 12px; cursor: pointer; outline: none;
}
.range-btn.is-active { background: #0066b3; color: #fff; }

.chart-wrap { width: 100%; overflow: hidden; }
.chart-svg { width: 100%; height: 260px; display: block; }
.axis-text { font-size: 10px; fill: #9aa4ae; }
.chart-empty { padding: 48px 0; text-align: center; color: #6c757d; }

.status-row { display: flex; align-items: center; margin-bottom: 10px; }
.status-name { flex: 0 0 84px; font-size: 13px; color: #6c757d; }
.status-track { flex: 1; height: 8px; background: #eef1f4; border-radius: 4px; overflow: hidden; margin: 0 10px; }
.status-fill { height: 100%; border-radius: 4px; }
.status-fill.is-paid { background: #00a800; }
.status-fill.is-pending { background: #e08b00; }
.status-fill.is-failed { background: #dc3545; }
.status-count { flex: 0 0 32px; text-align: right; font-weight: 600; font-size: 13px; }

.sandbox-empty {
  display: flex;
  align-items: flex-start;
  background: #fff8e6;
  border: 1px solid #ffe2a8;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 18px;
  font-size: 13px;
  color: #7a5b12;
}
.sandbox-empty .material-icons { font-size: 18px; margin-right: 10px; flex: 0 0 auto; }
.sandbox-empty .link-btn {
  border: 0; background: none; padding: 0; margin-left: 6px;
  color: #0066b3; font-weight: 600; cursor: pointer; text-decoration: underline;
}
.status-fill.is-cancelled { background: #b8c0cc; }
.status-pill.is-cancelled { background: #eef0f4; color: #6b7280; }
.fee-note { font-size: 11px; color: #8a94a6; line-height: 1.2; }
.mini-table { width: 100%; font-size: 13px; }
.mini-table th {
  text-align: left; color: #6c757d; font-weight: 600; font-size: 12px;
  border-bottom: 1px solid #eef1f4; padding: 6px 4px;
}
.mini-table td { padding: 8px 4px; border-bottom: 1px solid #f6f7f9; }
.mini-table tr:last-child td { border-bottom: none; }
.text-right { text-align: right; }
.ellipsis { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.view-all { font-size: 13px; font-weight: 600; color: #0066b3; }

.status-pill {
  font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 600; text-transform: capitalize;
}
.status-pill.is-success { background: #e3f7e3; color: #00a800; }
.status-pill.is-pending { background: #fdf3e0; color: #e08b00; }
.status-pill.is-failed { background: #fde3e5; color: #dc3545; }
</style>
